import { useEffect, useMemo, useState } from "react";
import { Provider, useDispatch, useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";

import "../../../App.css";
import "../CustomPuzzle.css";
import AppGameContainer from "../../AppGameContainer";
import { createCustomPuzzleStore } from "../../../redux/customPuzzleStore";
import { setAllNewsies } from "../../../redux/features/gameSlice";
import { CONFIG } from "../../../util/constants";

const { SERVER_API } = CONFIG;
const SELECTED_NEWSIE = "top-newsie";

const formatTime = (seconds) =>
  `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;

const sharePuzzle = async (title, setShareLabel) => {
  const url = window.location.href;
  const text = `Can you solve my Newsie study puzzle: ${title}?`;
  if (navigator.share) {
    navigator.share({ title, text, url }).catch(() => {});
    return;
  }
  try {
    await navigator.clipboard.writeText(`${text} ${url}`);
    setShareLabel("Link copied!");
  } catch (err) {
    setShareLabel(url);
  }
};

const CustomPuzzleGame = ({ puzzle }) => {
  const dispatch = useDispatch();
  const [shareLabel, setShareLabel] = useState("Share this puzzle");

  useEffect(() => {
    dispatch(setAllNewsies({ [SELECTED_NEWSIE]: puzzle.entries }));
  }, [puzzle]);

  const dataLoaded = useSelector((state: any) => Boolean(state.gameReducer.words?.[SELECTED_NEWSIE]));
  const winner = useSelector((state: any) => state.gameReducer.winner?.[SELECTED_NEWSIE]);
  const time = useSelector((state: any) => state.gameReducer.timer?.[SELECTED_NEWSIE]?.time || 0);
  const misses = useSelector((state: any) => state.gameReducer.incorrectGuesses?.[SELECTED_NEWSIE] || 0);

  if (!dataLoaded && !winner) return null;

  return (
    <>
      <AppGameContainer selectedNewsie={SELECTED_NEWSIE} customPuzzle={puzzle} />
      {winner && (
        <div className="custom-puzzle-result">
          <div className="custom-puzzle-result-title">🎉 You remembered them all!</div>
          <div className="custom-puzzle-result-stats">
            ⏱ {formatTime(time)} · ✕ {misses} {misses === 1 ? "miss" : "misses"}
          </div>
          <button
            className="custom-puzzle-button primary"
            onClick={() => sharePuzzle(puzzle.title, setShareLabel)}
          >
            {shareLabel}
          </button>
          <div className="custom-puzzle-result-links">
            <Link to="/create">Make your own</Link>
            <a href="/">Play today's Newsie</a>
          </div>
        </div>
      )}
    </>
  );
};

const CustomPuzzlePage = () => {
  const { puzzleId } = useParams();
  const [puzzle, setPuzzle] = useState<any>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "missing" | "error">("loading");
  const store = useMemo(() => createCustomPuzzleStore(), [puzzleId]);

  useEffect(() => {
    setStatus("loading");
    fetch(`${SERVER_API}/custom-puzzles/${encodeURIComponent(puzzleId || "")}`)
      .then(async (res) => {
        if (res.status === 404) return setStatus("missing");
        if (!res.ok) return setStatus("error");
        setPuzzle(await res.json());
        setStatus("ready");
      })
      .catch(() => setStatus("error"));
  }, [puzzleId]);

  if (status !== "ready") {
    return (
      <div className="App">
        <div className="custom-puzzle-message">
          {status === "loading" && "Loading puzzle…"}
          {status === "missing" && "We couldn't find that puzzle."}
          {status === "error" && "Couldn't load the puzzle. Check your connection and try again."}
          {status !== "loading" && (
            <div className="custom-puzzle-result-links">
              <Link to="/create">Make a puzzle</Link>
              <a href="/">Play today's Newsie</a>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <Provider store={store}>
      <div className="App">
        <CustomPuzzleGame puzzle={puzzle} />
      </div>
    </Provider>
  );
};

export default CustomPuzzlePage;
